import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { ComponentProps, forwardRef } from 'react'

import cn from '@/utilities/cn'

type TextInputProps = ComponentProps<'input'> & {
  error?: string
  name: string
  label?: string
  description?: string
  descriptionLocation?: 'before' | 'after'
  type?: string
  disabled?: boolean
  boldHeaders?: boolean
  placeholderIcon?: any
  required?: boolean
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      error,
      className,
      label,
      name,
      description,
      descriptionLocation = 'before',
      disabled,
      required,
      placeholderIcon,
      boldHeaders = false,
      type = 'text',
      ...rest
    }: TextInputProps,
    ref
  ) => {
    return (
      <div>
        {label && (
          <div className="row">
            <label
              htmlFor={name}
              className={cn('block text-sm font-medium leading-6 text-gray-900', { 'font-bold': boldHeaders })}
            >
              {label}
            </label>
            {required && <span className="ml-1 text-red-500">*</span>}
          </div>
        )}
        {description && descriptionLocation === 'before' && (
          <span className="text-sm text-zinc-500">{description}</span>
        )}
        <div className="relative mt-2 rounded-md shadow-sm">
          {placeholderIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              {placeholderIcon}
            </div>
          )}
          <input
            id={name}
            name={name}
            ref={ref}
            type={type}
            disabled={disabled}
            className={cn(
              disabled ? 'bg-gray-50' : '',
              error ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-blue-600',
              placeholderIcon ? 'pl-10' : '',
              'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-1 focus:ring-inset sm:text-sm sm:leading-6',
              className
            )}
            {...rest}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        {description && descriptionLocation === 'after' && <span className="text-sm text-zinc-500">{description}</span>}
      </div>
    )
  }
)

TextInput.displayName = 'TextInput'

export default TextInput
